import { type FunctionReference, anyApi } from "convex/server";
import { type GenericId as Id } from "convex/values";

export const api: PublicApiType = anyApi as unknown as PublicApiType;
export const internal: InternalApiType = anyApi as unknown as InternalApiType;

export type PublicApiType = {
  queries: {
    festivals: {
      featuredFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          festival: {
            dynamic_link?: string;
            id: number;
            name: string;
            published_at?: string;
            searchable?: boolean;
            slug: string;
          };
          latest_edition: {
            _id: Id<"editions">;
            address: {
              city: string;
              full_address: string;
              latitude: number;
              longitude: number;
            };
            cover_background_url: string;
            end_date: string;
            id: number;
            published_at?: string;
            searchable?: boolean;
            slug: string;
            start_date: string;
            year: number;
          };
        }>
      >;
      upcomingFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          festival: { id: number; name: string; slug: string };
          latest_edition: {
            _id: Id<"editions">;
            address: {
              city: string;
              full_address: string;
              latitude: number;
              longitude: number;
            };
            cover_background_url: string;
            end_date: string;
            id: number;
            published_at?: string;
            searchable?: boolean;
            slug: string;
            start_date: string;
            year: number;
          };
        }>
      >;
      upcomingFestivalsInfinite: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
      festivalsCountByCountry: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      festivalsByCountry: FunctionReference<
        "query",
        "public",
        { countrySlug: string },
        any
      >;
      festivalBySlug: FunctionReference<
        "query",
        "public",
        { slug: string },
        any
      >;
      allFestivals: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
      searchFestivals: FunctionReference<
        "query",
        "public",
        { query: string },
        any
      >;
      latestEditionIdBySlug: FunctionReference<
        "query",
        "public",
        { slug: string },
        any
      >;
      latestEditionByFestivalId: FunctionReference<
        "query",
        "public",
        { festivalId: number | Id<"festivals"> },
        any
      >;
    };
    artists: {
      popularArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      popularArtistsInfinite: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
      popularArtistsByPage: FunctionReference<
        "query",
        "public",
        { limit: number; page: number },
        any
      >;
      artistBySlug: FunctionReference<
        "query",
        "public",
        { locale: string; slug: string },
        any
      >;
      allArtists: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
    };
    users: {
      getOrganizerFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      profile: FunctionReference<"query", "public", Record<string, never>, any>;
      getUserInfo: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      getTopUsersByPoints: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
    };
    countries: {
      countriesWithPublishedFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      editionsForCountryIndex: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
      countriesByIds: FunctionReference<
        "query",
        "public",
        { ids: Array<string> },
        any
      >;
    };
    backstage: {
      preview: {
        getEditionHeader: FunctionReference<
          "query",
          "public",
          { editionId: number; festivalSlug: string },
          any
        >;
        getEditionPreviewLink: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        showEditionHeaderAlert: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
        getFestivalHeader: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
        editionStatus: FunctionReference<
          "query",
          "public",
          { editionId: number; festivalSlug: string },
          any
        >;
        getEditionAdsPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionAnnouncementsPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionDesign: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionInformation: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getFestivalSettings: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      artists: {
        getEditionArtistNames: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        listEditionArtists: FunctionReference<
          "query",
          "public",
          {
            editionId: number;
            paginationOpts: {
              cursor: string | null;
              endCursor?: string | null;
              id?: number;
              maximumBytesRead?: number;
              maximumRowsRead?: number;
              numItems: number;
            };
          },
          any
        >;
        getEditionArtistsCategories: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionPlaylist: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      places: {
        getEditionPlaces: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getPlace: FunctionReference<
          "query",
          "public",
          { editionId: number; placeId: number },
          any
        >;
        getPlaceTypes: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getPlaceTypesManagement: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionPlacesPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      schedules: {
        getEditionDates: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionSchedules: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionPlaces: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionSchedulesCategories: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      events: {
        getEditionEventsPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionEvents: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionEventNames: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionEvent: FunctionReference<
          "query",
          "public",
          { editionId: number; eventId: number },
          any
        >;
        getEditionEventsCategories: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionArtistsNames: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      faq: {
        getEditionFaqPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionFaq: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionFaqItem: FunctionReference<
          "query",
          "public",
          { editionId: number; faqId: number },
          any
        >;
        getEditionFaqCategories: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      sponsors: {
        getEditionSponsors: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionSponsorCategories: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getSponsor: FunctionReference<
          "query",
          "public",
          { editionId: number; sponsorId: number },
          any
        >;
        getEditionSponsorsPreview: FunctionReference<
          "query",
          "public",
          { editionId: number },
          { totalCount: number }
        >;
      };
      informations: {
        getEditionInformation: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getCountries: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          Array<{ _id: string; code: string; id: string; name: string }>
        >;
        getEditionLocales: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionFeatures: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionStatus: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      announcements: {
        getEditionAnnouncements: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionAnnouncement: FunctionReference<
          "query",
          "public",
          { announcementId: number; editionId: number },
          any
        >;
      };
      design: {
        getEditionDesign: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getFileByHash: FunctionReference<
          "query",
          "public",
          { hash: string },
          any
        >;
      };
      editionAds: {
        getEditionAdsBanners: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionAdsBanner: FunctionReference<
          "query",
          "public",
          { bannerId: string; editionId: number },
          any
        >;
        getPlacementTypes: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          any
        >;
        getBannerStats: FunctionReference<
          "query",
          "public",
          { bannerId: string; editionId: number },
          any
        >;
        getAdsFeatureStatus: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      export: {
        exportEditionArtists: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        exportEditionEvents: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        exportEditionSchedules: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        exportEditionPlaces: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      maps: {
        getEditionMap: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionInformation: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      contributions: {
        getEditionBySlug: FunctionReference<
          "query",
          "public",
          { editionSlug: string; festivalSlug: string },
          any
        >;
        getFestivalsNeedingWork: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          any
        >;
        getUserContributions: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          any
        >;
        getEditionArtistsAndContributions: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionPlacesAndContributions: FunctionReference<
          "query",
          "public",
          { editionId: Id<"editions"> },
          any
        >;
        getEditionSchedulesAndContributions: FunctionReference<
          "query",
          "public",
          { editionId: Id<"editions"> },
          any
        >;
        getEditionArtistsName: FunctionReference<
          "query",
          "public",
          { editionId: Id<"editions"> },
          any
        >;
        getEditionDates: FunctionReference<
          "query",
          "public",
          { editionId: Id<"editions"> },
          any
        >;
        searchCrewAvailableFestivals: FunctionReference<
          "query",
          "public",
          { search: string },
          any
        >;
      };
      festival: {
        getFestivalDashboard: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
        getFestivalUsers: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
        addUserToFestival: FunctionReference<
          "mutation",
          "public",
          {
            festivalSlug: string;
            locale: string;
            origin: string;
            userEmail: string;
          },
          any
        >;
        removeUserFromFestival: FunctionReference<
          "mutation",
          "public",
          { festivalSlug: string; userId: number },
          any
        >;
      };
      editions: {
        getLatestEdition: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
      };
      import: {
        getPreviousEditions: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getImportSponsorsData: FunctionReference<
          "query",
          "public",
          { currentEditionId: number; sourceEditionId: number },
          any
        >;
        getImportFaqData: FunctionReference<
          "query",
          "public",
          { currentEditionId: number; sourceEditionId: number },
          any
        >;
        getImportMapData: FunctionReference<
          "query",
          "public",
          { currentEditionId: number; sourceEditionId: number },
          any
        >;
        getImportPlacesData: FunctionReference<
          "query",
          "public",
          { currentEditionId: number; sourceEditionId: number },
          any
        >;
      };
      shell: {
        getProtectedEditionShell: FunctionReference<
          "query",
          "public",
          { editionSlug: string; festivalSlug: string },
          any
        >;
        getDraftSections: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      editionAssets: {
        getAssetsTemplates: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          Array<{
            _id: string;
            category: string;
            height: number;
            name: string;
            templateId: string;
            variableMapping: any;
            width: number;
          }>
        >;
        getEditionMetaForAssets: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getGalleryItems: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionOverrides: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditableVariablesForCategory: FunctionReference<
          "query",
          "public",
          { category: string },
          Array<{ defaultValue: string; name: string }>
        >;
        getAsset: FunctionReference<
          "query",
          "public",
          { id: Id<"edition_assets"> },
          any
        >;
      };
      social: {
        getAssetGenerationContext: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getAssetPostCandidates: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      onboarding: {
        getOnboardingData: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
      };
      activations: {
        getEditionActivations: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getActivationById: FunctionReference<
          "query",
          "public",
          { activationId: Id<"activations"> },
          any
        >;
        getActivationSubmissions: FunctionReference<
          "query",
          "public",
          {
            activationId: Id<"activations">;
            cursor?: string | null;
            limit?: number;
          },
          any
        >;
      };
      editionRewards: {
        listEditionRewards: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
        getEditionRewardById: FunctionReference<
          "query",
          "public",
          { rewardId: Id<"edition_rewards"> },
          any
        >;
      };
      importSystem: {
        getConnection: FunctionReference<
          "query",
          "public",
          { editionId: number; provider: "festigest" | "zonefestival" },
          any
        >;
        getMapping: FunctionReference<
          "query",
          "public",
          { editionId: number; provider: "festigest" | "zonefestival" },
          any
        >;
        getSourceSchema: FunctionReference<
          "query",
          "public",
          { runId: Id<"import_runs"> },
          any
        >;
        getRun: FunctionReference<
          "query",
          "public",
          { runId: Id<"import_runs"> },
          any
        >;
        listRuns: FunctionReference<
          "query",
          "public",
          { editionId: number; provider: "festigest" | "zonefestival" },
          any
        >;
        listDestinations: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          any
        >;
        listEditionsWithMapping: FunctionReference<
          "query",
          "public",
          { editionId: number; provider: "festigest" | "zonefestival" },
          any
        >;
      };
    };
    app: {
      editions: {
        edition: FunctionReference<"query", "public", { id: number }, any>;
        lastUpdate: FunctionReference<"query", "public", { id: number }, any>;
      };
      feed: {
        feedFilters: FunctionReference<
          "query",
          "public",
          Record<string, never>,
          any
        >;
        feed: FunctionReference<
          "query",
          "public",
          { country?: string; music_types?: Array<number>; offset?: number },
          any
        >;
      };
      artists: {
        artist: FunctionReference<"query", "public", { id: number }, any>;
        artistEditions: FunctionReference<
          "query",
          "public",
          { id: number },
          any
        >;
        artists: FunctionReference<
          "query",
          "public",
          {
            editionId: number;
            paginationOpts?: { cursor?: string; numItems: number };
          },
          any
        >;
      };
      schedules: {
        editionSchedules: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      events: {
        events: FunctionReference<
          "query",
          "public",
          {
            editionId: number;
            paginationOpts?: { cursor?: string; numItems: number };
          },
          any
        >;
      };
      announcements: {
        editionAnnouncements: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      faq: {
        faq: FunctionReference<"query", "public", { editionId: number }, any>;
      };
      sponsors: {
        editionSponsors: FunctionReference<
          "query",
          "public",
          { id: number },
          any
        >;
      };
      places: {
        editionPlaces: FunctionReference<
          "query",
          "public",
          { id: number },
          any
        >;
        editionPlaceTypes: FunctionReference<
          "query",
          "public",
          { editionId: number },
          any
        >;
      };
      map: {
        map: FunctionReference<"query", "public", { editionId: number }, any>;
      };
      users: {
        auth: FunctionReference<
          "action",
          "public",
          {
            editionArtistsIds?: Array<number>;
            editionEventsIds?: Array<number>;
            email: string;
            festivalsIds?: Array<number>;
            firebaseUid: string;
            uniqueArtistsIds?: Array<number>;
          },
          any
        >;
        getMe: FunctionReference<
          "query",
          "public",
          { firebaseUid: string },
          any
        >;
        updateProfile: FunctionReference<
          "mutation",
          "public",
          {
            firebaseUid: string;
            first_name?: null | string;
            last_name?: null | string;
            username?: null | string;
          },
          any
        >;
        deleteAccount: FunctionReference<
          "mutation",
          "public",
          { firebaseUid: string },
          any
        >;
        updateFavorites: FunctionReference<
          "mutation",
          "public",
          {
            editionArtistsIds?: Array<number>;
            editionEventsIds?: Array<number>;
            festivalsIds?: Array<number>;
            firebaseUid: string;
            uniqueArtistsIds?: Array<number>;
          },
          any
        >;
        syncFavorites: FunctionReference<
          "query",
          "public",
          {
            editionArtistsIds: Array<number>;
            editionEventsIds: Array<number>;
            festivalsIds: Array<number>;
            lastSyncTimestamp: string;
            uniqueArtistsIds: Array<number>;
          },
          any
        >;
      };
    };
    webWidget: {
      artists: FunctionReference<"query", "public", { editionId: number }, any>;
      artist: FunctionReference<
        "query",
        "public",
        { artistId: number; editionId: number },
        any
      >;
      schedules: FunctionReference<
        "query",
        "public",
        { editionId: number },
        any
      >;
      event: FunctionReference<
        "query",
        "public",
        { editionId: number; eventId: number },
        any
      >;
      edition: FunctionReference<"query", "public", { editionId: number }, any>;
    };
    deeplink: {
      festivalBySlugLatestEdition: FunctionReference<
        "query",
        "public",
        { slug: string },
        any
      >;
      edition: FunctionReference<
        "query",
        "public",
        { id: number | Id<"editions"> },
        any
      >;
      editionRedirectSlugs: FunctionReference<
        "query",
        "public",
        { id: number | Id<"editions"> },
        any
      >;
      getFingerprint: FunctionReference<
        "action",
        "public",
        { fingerprint: string },
        null | {
          _creationTime: number;
          _id: Id<"deeplink_fingerprints">;
          created_at: string;
          deeplink: string;
          fingerprint: string;
          updated_at: string;
        }
      >;
      fingerprint: FunctionReference<
        "query",
        "public",
        { fingerprint: string },
        any
      >;
      upsertFingerprint: FunctionReference<
        "mutation",
        "public",
        { deeplink: string; fingerprint: string },
        any
      >;
      deleteFingerprint: FunctionReference<
        "mutation",
        "public",
        { fingerprint: string },
        boolean
      >;
    };
    admin: {
      users: {
        listFestivalManagers: FunctionReference<
          "query",
          "public",
          { festivalId: Id<"festivals"> },
          Array<{
            created_at: number;
            email: string;
            festUserId: Id<"fest_users">;
            first_name?: string;
            last_name?: string;
            username?: string;
          }>
        >;
        findFestUser: FunctionReference<
          "query",
          "public",
          { search: string },
          | {
              email: string;
              exists: true;
              festUserId: Id<"fest_users">;
              first_name?: string;
              last_name?: string;
              matchedBy: "email" | "username";
              username?: string;
            }
          | { exists: false }
        >;
        getUserByEmail: FunctionReference<
          "query",
          "public",
          { email: string },
          | {
              email: string;
              exists: true;
              festivals: Array<{ id: number; name: string; slug: string }>;
              id: number;
            }
          | { exists: false }
        >;
      };
      artists: {
        getUniqueArtistBySlug: FunctionReference<
          "query",
          "public",
          { slug: string },
          any
        >;
      };
      festivalFeatures: {
        getFestivalFeatures: FunctionReference<
          "query",
          "public",
          { festivalSlug: string },
          any
        >;
      };
      festivals: {
        list: FunctionReference<
          "query",
          "public",
          {
            paginationOpts: {
              cursor: string | null;
              endCursor?: string | null;
              id?: number;
              maximumBytesRead?: number;
              maximumRowsRead?: number;
              numItems: number;
            };
          },
          any
        >;
      };
      crawlerRuns: {
        list: FunctionReference<
          "query",
          "public",
          {
            paginationOpts: {
              cursor: string | null;
              endCursor?: string | null;
              id?: number;
              maximumBytesRead?: number;
              maximumRowsRead?: number;
              numItems: number;
            };
          },
          any
        >;
      };
    };
    editions: {
      editionBySlug: FunctionReference<
        "query",
        "public",
        { editionSlug: string; festivalSlug: string },
        any
      >;
      allEditions: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
    };
    festivalWidgets: {
      getEditionsByLicenseKey: FunctionReference<
        "query",
        "public",
        { licenseKey: string },
        any
      >;
      getArtistCategoriesByLicenseKey: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; licenseKey: string },
        Array<{ _id: Id<"edition_artists_categories">; name: string }>
      >;
      getEventsCategoriesByLicenseKey: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; licenseKey: string },
        Array<{ _id: Id<"events_categories">; name: string }>
      >;
      getScheduleCategoriesByLicenseKey: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; licenseKey: string },
        Array<{ _id: Id<"edition_schedules_categories">; name: string }>
      >;
      getPlaceTypesByLicenseKey: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; licenseKey: string },
        Array<{ _id: Id<"place_types">; name: string }>
      >;
      getEditionColorsByApiKey: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; licenseKey: string },
        any
      >;
    };
    widget: {
      edition: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        null | {
          _id: Id<"editions">;
          cover_background: null | { formats?: any; url?: string };
          day_start_time: string;
          default_locale_code: string;
          design?: {
            colors: {
              primary: string;
              primaryAltOne: string;
              primaryAltOneForeground: string;
              primaryAltThree: string;
              primaryAltThreeForeground: string;
              primaryAltTwo: string;
              primaryAltTwoForeground: string;
              primaryForeground: string;
            };
            hide_cover_logo?: boolean;
            home_background_shade_percentage?: number;
          };
          end_date: string;
          festival: null | { dynamic_link?: string; id: number; name: string };
          id: number;
          show_artists?: boolean;
          show_event_times?: boolean;
          start_date: string;
          timezone_offset_minutes: number;
        }
      >;
      artists: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_artists">;
          categories: Array<{
            _id: Id<"edition_artists_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          id: number;
          image: null | { formats?: any; url?: string };
          name: string;
          order?: number;
          schedules: Array<{ start_date: string }>;
          unique_artist: null | { spotify_popularity?: number };
        }>
      >;
      artist: FunctionReference<
        "query",
        "public",
        { artistId: Id<"edition_artists">; editionId: Id<"editions"> },
        null | {
          _id: Id<"edition_artists">;
          categories: Array<{
            _id: Id<"edition_artists_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          custom_fields: Array<{
            _id: Id<"custom_fields">;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          id: number;
          image: null | { formats?: any; url?: string };
          links: Array<{ url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          name: string;
          schedules: Array<{
            _id: Id<"edition_schedules">;
            end_time?: string;
            event: null | {
              _id: Id<"edition_events">;
              id: number;
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            id: number;
            place: null | {
              _id: Id<"places">;
              id: number;
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            start_date: string;
            start_time?: string;
            ticket_url?: string;
          }>;
          unique_artist: null | { spotify_id?: string };
        }
      >;
      schedules: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_schedules">;
          artist: null | {
            _id: Id<"edition_artists">;
            id: number;
            name: string;
          };
          categories: Array<{
            _id: Id<"edition_schedules_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          end_time?: string;
          event: null | {
            _id: Id<"edition_events">;
            id: number;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
          };
          id: number;
          place: {
            _id: Id<"places">;
            id: number;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          };
          start_date: string;
          start_time?: string;
          ticket_url?: string;
        }>
      >;
      events: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_events">;
          categories: Array<{
            _id: Id<"events_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          id: number;
          image: null | { formats?: any; url?: string };
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
        }>
      >;
      event: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; eventId: Id<"edition_events"> },
        null | {
          _id: Id<"edition_events">;
          artists: Array<{
            _id: Id<"edition_artists">;
            id: number;
            image: null | { formats?: any; url?: string };
            name: string;
          }>;
          categories: Array<{
            _id: Id<"events_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          custom_fields: Array<{
            _id: Id<"custom_fields">;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          id: number;
          image: null | { formats?: any; url?: string };
          links: Array<{ platform?: string; url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          schedules: Array<{
            _id: Id<"edition_schedules">;
            end_time?: string;
            id: number;
            place: null | {
              _id: Id<"places">;
              id: number;
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            start_date: string;
            start_time?: string;
            ticket_url?: string;
          }>;
        }
      >;
      places: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"places">;
          id: number;
          image: null | { formats?: any; url?: string };
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          order?: number;
          place_type: null | {
            _id: Id<"place_types">;
            icon: null | { formats?: any; url?: string };
            id: number;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
            show_in_list: boolean;
          };
        }>
      >;
      place: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; placeId: Id<"places"> },
        null | {
          _id: Id<"places">;
          id: number;
          image: null | { formats?: any; url?: string };
          links: Array<{ platform?: string; url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          maps_pin: null | { latitude?: number; longitude?: number };
          place_type: null | {
            _id: Id<"place_types">;
            icon: null | { formats?: any; url?: string };
            id: number;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
            show_in_list: boolean;
          };
        }
      >;
    };
    calendar: {
      calendarEditions: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          country?: { code: string; name: string; slug: string };
          edition: {
            city?: string;
            cover_background_url?: string;
            end_date: string;
            primary_color?: string;
            slug: string;
            start_date: string;
            year: number;
          };
          festival: { name: string; slug: string };
        }>
      >;
    };
    getStarted: {
      hasFestivalOrganizer: FunctionReference<
        "query",
        "public",
        { festivalSlug: string },
        any
      >;
      festivalClaimInfo: FunctionReference<
        "query",
        "public",
        { slug: string },
        any
      >;
    };
    genres: {
      publishedFestivalsForGenresIndex: FunctionReference<
        "query",
        "public",
        {
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
      festivalsByGenrePaginated: FunctionReference<
        "query",
        "public",
        {
          genreSlug: string;
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        any
      >;
    };
  };
  mutations: {
    profile: {
      updateProfile: FunctionReference<
        "mutation",
        "public",
        { first_name: string; last_name: string; username: string },
        any
      >;
    };
    editions: {
      updateEditionCrewStatus: FunctionReference<
        "mutation",
        "public",
        {
          crew_status?:
            | "waiting_for_announcement"
            | "work_in_progress"
            | "nearly_done"
            | "completed";
          editionId: number;
        },
        any
      >;
      updateEditionStatus: FunctionReference<
        "mutation",
        "public",
        { editionId: number; searchable: boolean },
        any
      >;
      updateEditionDisplaySettings: FunctionReference<
        "mutation",
        "public",
        {
          display_artists_by_category?: boolean;
          editionId: number;
          show_artists?: boolean;
          show_event_times?: boolean;
          show_events?: boolean;
          show_stages?: boolean;
        },
        any
      >;
      createEdition: FunctionReference<
        "mutation",
        "public",
        {
          address_id: Id<"address"> | null;
          country_id: string;
          end_date: string;
          festivalSlug: string;
          start_date: string;
          timezone_offset_minutes: number;
        },
        { editionId: number; editionSlug: string }
      >;
      createNextEditionAsManager: FunctionReference<
        "mutation",
        "public",
        {
          address_id: Id<"address"> | null;
          country_id: string;
          end_date: string;
          festivalSlug: string;
          start_date: string;
          timezone_offset_minutes: number;
        },
        { editionId: number; editionSlug: string }
      >;
    };
    links: {
      addSocialLink: FunctionReference<
        "mutation",
        "public",
        { editionId: number; socialLink: { url: string } },
        any
      >;
      updateSocialLink: FunctionReference<
        "mutation",
        "public",
        { editionId: number; linkId: number; socialLink: { url: string } },
        any
      >;
      removeSocialLink: FunctionReference<
        "mutation",
        "public",
        { editionId: number; linkId: number },
        any
      >;
      updateContactLink: FunctionReference<
        "mutation",
        "public",
        { contactLink?: { url: string }; editionId: number },
        any
      >;
      updateWebsiteLink: FunctionReference<
        "mutation",
        "public",
        { editionId: number; websiteLink?: { url: string } },
        any
      >;
    };
    artists: {
      bulkUpdateArtists: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete" | "assign_category";
          artistIds: Array<number>;
          categoryIds?: Array<number>;
          editionId: number;
        },
        any
      >;
      createEditionArtist: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          publishedAt?: string;
          uniqueArtistId: Id<"unique_artists">;
        },
        any
      >;
      addUniqueArtistToEdition: FunctionReference<
        "mutation",
        "public",
        { editionId: number; uniqueArtistId: Id<"unique_artists"> },
        any
      >;
      deleteEditionArtist: FunctionReference<
        "mutation",
        "public",
        { editionArtistId: Id<"edition_artists"> },
        any
      >;
      addArtistToEdition: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          festUserId?: Id<"fest_users">;
          isContribution?: boolean;
          spotifyId?: string;
          uniqueArtistId?: string;
        },
        any
      >;
      upsertEditionArtist: FunctionReference<
        "mutation",
        "public",
        {
          artistId?: number;
          categories?: Array<number>;
          custom_fields?: Array<{
            id?: number;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          editionId: number;
          hidden_unique_link_ids?: Array<number>;
          image_id?: number | null;
          links?: Array<{ blocked?: boolean; id?: number; url: string }>;
          localized_fields?: Array<{
            biography?: string;
            is_default_locale: boolean;
            locale: string;
          }>;
          name: string;
          order?: number;
        },
        any
      >;
      updateEditionPlaylist: FunctionReference<
        "mutation",
        "public",
        { editionId: number; playlist: string },
        any
      >;
      removeEditionPlaylist: FunctionReference<
        "mutation",
        "public",
        { editionId: number },
        any
      >;
      upsertEditionArtistsCategory: FunctionReference<
        "mutation",
        "public",
        {
          categoryId?: number;
          editionId: number;
          home_preview?: boolean;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
        },
        any
      >;
      deleteEditionArtistsCategory: FunctionReference<
        "mutation",
        "public",
        { categoryId: number; editionId: number },
        any
      >;
      updateArtistsOrder: FunctionReference<
        "mutation",
        "public",
        { editionId: number; itemOrders: Array<{ id: number; order: number }> },
        { success: boolean }
      >;
      updateArtistCategoriesOrder: FunctionReference<
        "mutation",
        "public",
        { categories: Array<{ id: number; order: number }>; editionId: number },
        any
      >;
    };
    files: {
      addFile: FunctionReference<
        "mutation",
        "public",
        {
          alternative_text?: string;
          caption?: string;
          ext?: string;
          formats: Array<{ breakpoint: string; hash: string; url?: string }>;
          hash: string;
          height?: number;
          mime?: string;
          name: string;
          size?: number;
          url?: string;
          width?: number;
        },
        any
      >;
    };
    uniqueArtists: {
      updateUniqueArtist: FunctionReference<
        "mutation",
        "public",
        {
          id: Id<"unique_artists">;
          imageId?: number;
          links?: Array<{ id?: number; url: string }>;
          localizedFields?: Array<{ biography: string; locale: string }>;
          name?: string;
        },
        any
      >;
      createUniqueArtistManual: FunctionReference<
        "action",
        "public",
        {
          imageId?: number;
          links: Array<{ label: string; url: string }>;
          name: string;
        },
        any
      >;
    };
    places: {
      bulkUpdatePlaces: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete";
          editionId: number;
          placeIds: Array<number>;
        },
        any
      >;
      upsertPlace: FunctionReference<
        "mutation",
        "public",
        {
          description?: string;
          editionId: number;
          image_id?: number | null;
          localized_fields?: Array<{
            description?: string;
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          name: string;
          order?: number;
          placeId?: number;
          place_type_id?: number;
        },
        any
      >;
      upsertPlaceType: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          home_preview?: boolean;
          icon_id?: number;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
          placeTypeId?: number;
          show_in_list?: boolean;
        },
        any
      >;
      deletePlaceType: FunctionReference<
        "mutation",
        "public",
        { editionId: number; placeTypeId: number },
        any
      >;
      reorderPlaceTypes: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          placeTypeOrders: Array<{ id: number; order: number }>;
        },
        any
      >;
      updatePlacesOrder: FunctionReference<
        "mutation",
        "public",
        { editionId: number; itemOrders: Array<{ id: number; order: number }> },
        any
      >;
    };
    schedules: {
      upsertSchedule: FunctionReference<
        "mutation",
        "public",
        {
          artistId?: number;
          categories?: Array<number>;
          editionId: number;
          endTime?: string;
          eventId?: number;
          placeId?: number;
          scheduleId?: number;
          startDate: string;
          startTime?: string;
          ticketUrl?: string;
        },
        any
      >;
      deleteSchedule: FunctionReference<
        "mutation",
        "public",
        { editionId: number; scheduleId: number },
        any
      >;
      updateScheduleStatus: FunctionReference<
        "mutation",
        "public",
        { editionId: number; publish: boolean; scheduleId: number },
        any
      >;
      bulkUpdateSchedules: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete" | "assign_category";
          categoryIds?: Array<number>;
          editionId: number;
          scheduleIds: Array<number>;
        },
        any
      >;
      upsertEditionSchedulesCategory: FunctionReference<
        "mutation",
        "public",
        {
          categoryId?: number;
          editionId: number;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
          slug?: string;
          view?: string;
        },
        any
      >;
      deleteEditionSchedulesCategory: FunctionReference<
        "mutation",
        "public",
        { categoryId: number; editionId: number },
        any
      >;
      updateSchedulesCategoriesOrder: FunctionReference<
        "mutation",
        "public",
        { categories: Array<{ id: number; order: number }>; editionId: number },
        any
      >;
    };
    events: {
      bulkUpdateEvents: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete" | "assign_category";
          categoryIds?: Array<number>;
          editionId: number;
          eventIds: Array<number>;
        },
        any
      >;
      upsertEditionEvent: FunctionReference<
        "mutation",
        "public",
        {
          artists?: Array<{ id: number; order: number }>;
          categories?: Array<number>;
          custom_fields?: Array<{
            id?: number;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          editionId: number;
          eventId?: number;
          image_id?: number | null;
          links?: Array<{ id?: number; url: string }>;
          localized_fields?: Array<{
            description?: string;
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
        },
        any
      >;
      upsertEditionEventsCategory: FunctionReference<
        "mutation",
        "public",
        {
          categoryId?: number;
          editionId: number;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
        },
        any
      >;
      deleteEditionEventsCategory: FunctionReference<
        "mutation",
        "public",
        { categoryId: number; editionId: number },
        any
      >;
      updateCategoriesOrder: FunctionReference<
        "mutation",
        "public",
        {
          categoryOrders: Array<{ id: number; order: number }>;
          editionId: number;
        },
        any
      >;
      updateEventsOrder: FunctionReference<
        "mutation",
        "public",
        { editionId: number; itemOrders: Array<{ id: number; order: number }> },
        any
      >;
    };
    faq: {
      bulkUpdateFaq: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete";
          editionId: number;
          faqIds: Array<number>;
        },
        any
      >;
      upsertEditionFaqItem: FunctionReference<
        "mutation",
        "public",
        {
          answer: string;
          categoryId: number;
          editionId: number;
          faqId?: number;
          link?: { id?: number; url: string };
          localized_fields?: Array<{
            answer?: string;
            is_default_locale: boolean;
            locale: string;
            question?: string;
          }>;
          order?: number;
          question: string;
        },
        any
      >;
      upsertEditionFaqCategory: FunctionReference<
        "mutation",
        "public",
        {
          categoryId?: number;
          editionId: number;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
        },
        any
      >;
      deleteEditionFaqCategory: FunctionReference<
        "mutation",
        "public",
        { categoryId: number; editionId: number },
        any
      >;
      updateFaqItemsOrder: FunctionReference<
        "mutation",
        "public",
        { editionId: number; itemOrders: Array<{ id: number; order: number }> },
        any
      >;
      updateCategoriesOrder: FunctionReference<
        "mutation",
        "public",
        {
          categoryOrders: Array<{ id: number; order: number }>;
          editionId: number;
        },
        any
      >;
    };
    sponsors: {
      upsertSponsor: FunctionReference<
        "mutation",
        "public",
        {
          categoryIds?: Array<Id<"sponsor_categories">>;
          dark_logo_id?: number;
          editionId: number;
          link_url?: string;
          logo_id?: number;
          name: string;
          order?: number;
          sponsorId?: number;
        },
        { isUpdate: boolean; sponsorId: number; success: boolean }
      >;
      deleteSponsor: FunctionReference<
        "mutation",
        "public",
        { editionId: number; sponsorId: number },
        { success: boolean }
      >;
      bulkUpdateSponsors: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete";
          editionId: number;
          sponsorIds: Array<number>;
        },
        { affectedCount: number; success: boolean }
      >;
      updateSponsorsOrder: FunctionReference<
        "mutation",
        "public",
        { editionId: number; itemOrders: Array<{ id: number; order: number }> },
        { success: boolean }
      >;
      upsertEditionSponsorCategory: FunctionReference<
        "mutation",
        "public",
        {
          categoryId?: Id<"sponsor_categories">;
          editionId: number;
          localized_fields?: Array<{
            is_default_locale: boolean;
            locale: string;
            name?: string;
          }>;
          order?: number;
        },
        any
      >;
      deleteEditionSponsorCategory: FunctionReference<
        "mutation",
        "public",
        { categoryId: Id<"sponsor_categories">; editionId: number },
        any
      >;
      updateSponsorCategoriesOrder: FunctionReference<
        "mutation",
        "public",
        {
          categoryOrders: Array<{
            id: Id<"sponsor_categories">;
            order: number;
          }>;
          editionId: number;
        },
        any
      >;
    };
    informations: {
      updateEditionInformation: FunctionReference<
        "mutation",
        "public",
        {
          address_id?: Id<"address">;
          bottom_bar_labels?: Array<{
            buttonText?: string;
            label?: string;
            locale: string;
          }>;
          country_id: string;
          editionId: number;
          end_date: string;
          start_date: string;
          timezone_offset_minutes: number;
          top_bar_link_type?: "automatic" | "tickets" | "cashless" | "custom";
        },
        { success: boolean }
      >;
      updateMerchandisingLink: FunctionReference<
        "mutation",
        "public",
        { editionId: number; merchandisingLink?: { url: string } },
        { success: boolean }
      >;
      updateAfterMovieLink: FunctionReference<
        "mutation",
        "public",
        { afterMovieLink?: { url: string }; editionId: number },
        { success: boolean }
      >;
      updateCashlessLink: FunctionReference<
        "mutation",
        "public",
        {
          cashlessLink?: {
            label?: string;
            localizations?: Array<{ label?: string; locale: string }>;
            url: string;
          };
          editionId: number;
        },
        { success: boolean }
      >;
      updateTicketLink: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          ticketLink?: {
            label?: string;
            localizations?: Array<{ label?: string; locale: string }>;
            url: string;
          };
        },
        { success: boolean }
      >;
      updateCustomLink: FunctionReference<
        "mutation",
        "public",
        {
          customLink?: {
            label?: string;
            localizations?: Array<{ label?: string; locale: string }>;
            url: string;
          };
          editionId: number;
        },
        { success: boolean }
      >;
      updateEditionLocales: FunctionReference<
        "mutation",
        "public",
        {
          default_locale_id: number;
          editionId: number;
          localeIds: Array<number>;
        },
        { success: boolean }
      >;
    };
    announcements: {
      upsertAnnouncement: FunctionReference<
        "mutation",
        "public",
        {
          announcementId?: number;
          deep_link?: string;
          display_web_view?: boolean;
          edition_id: number;
          link_url?: string;
          localized_fields?: Array<{
            content?: string;
            is_default_locale: boolean;
            locale: string;
            title?: string;
          }>;
          pinned?: boolean;
          published_at?: string;
          scheduled_publish_at?: string;
          with_notification?: boolean;
        },
        { announcementId: number; isUpdate: boolean; success: boolean }
      >;
      bulkUpdateAnnouncements: FunctionReference<
        "mutation",
        "public",
        {
          action: "draft" | "delete";
          announcementIds: Array<number>;
          editionId: number;
        },
        { affectedCount: number; success: boolean }
      >;
    };
    design: {
      updateEditionDesign: FunctionReference<
        "mutation",
        "public",
        {
          cover_background_id?: number;
          cover_logo_id?: number;
          editionId: number;
          hide_cover_logo?: boolean;
          home_background_shade_percentage?: number;
          logo_id?: number;
          primary_color: string;
        },
        any
      >;
    };
    editionAds: {
      upsertAdsBanner: FunctionReference<
        "mutation",
        "public",
        {
          bannerId?: string;
          editionId: number;
          images?: Array<{ image_id: number; locale: string }>;
          links?: Array<{ locale: string; url?: string }>;
          name: string;
          placements?: Array<{
            enabled: boolean;
            placement_type_id: string;
            target_ids?: Array<Id<"edition_artists"> | Id<"edition_events">>;
            weight: number;
          }>;
        },
        any
      >;
      bulkUpdateBanners: FunctionReference<
        "mutation",
        "public",
        {
          action: "publish" | "draft" | "delete";
          bannerIds: Array<string>;
          editionId: number;
        },
        any
      >;
      recordBannerView: FunctionReference<
        "mutation",
        "public",
        {
          placementId: string;
          userCountry?: string;
          userId?: number | Id<"fest_users">;
          userLocale?: string;
          userOs?: string;
        },
        any
      >;
      recordBannerClick: FunctionReference<
        "mutation",
        "public",
        {
          placementId: string;
          userCountry?: string;
          userId?: number | Id<"fest_users">;
          userLocale?: string;
          userOs?: string;
        },
        any
      >;
    };
    maps: {
      upsertPin: FunctionReference<
        "mutation",
        "public",
        {
          icon_id?: number;
          latitude: number;
          longitude: number;
          mapId: number;
          name: string;
          pinId?: number;
          place_id?: number | null;
          place_type_id?: number;
          size_modifier?: number;
        },
        any
      >;
      deletePin: FunctionReference<
        "mutation",
        "public",
        { pinId: number },
        any
      >;
      upsertPolygon: FunctionReference<
        "mutation",
        "public",
        {
          color: string;
          coordinates: any;
          extrusion_height?: number;
          extrusion_start?: number;
          mapId: number;
          name: string;
          pin_id?: number | null;
          polygonId?: number | null;
          zone_order?: number;
        },
        any
      >;
      deletePolygon: FunctionReference<
        "mutation",
        "public",
        { polygonId: number },
        any
      >;
      addMapIcon: FunctionReference<
        "mutation",
        "public",
        { editionId: number; fileId: number; name: string },
        any
      >;
      removeMapIcon: FunctionReference<
        "mutation",
        "public",
        { editionId: number; fileId: number },
        any
      >;
      setEditionImageMap: FunctionReference<
        "mutation",
        "public",
        { editionId: number; imageId: number },
        any
      >;
      removeEditionImageMap: FunctionReference<
        "mutation",
        "public",
        { editionId: number },
        any
      >;
      createEditionMap: FunctionReference<
        "mutation",
        "public",
        { editionId: number },
        any
      >;
      publishMapView: FunctionReference<
        "mutation",
        "public",
        {
          latitude: number;
          longitude: number;
          mapId: number;
          previewImageId?: number;
          zoom: number;
        },
        any
      >;
      unpublishMap: FunctionReference<
        "mutation",
        "public",
        { mapId: number },
        any
      >;
    };
    contributions: {
      deleteContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_artist_contributions"> },
        any
      >;
      removeQuickAddedArtist: FunctionReference<
        "mutation",
        "public",
        {
          editionId: number;
          spotifyId?: string;
          uniqueArtistId?: Id<"unique_artists">;
        },
        any
      >;
      approveContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_artist_contributions"> },
        any
      >;
      rejectContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_artist_contributions"> },
        any
      >;
      bulkUpdateContributions: FunctionReference<
        "mutation",
        "public",
        {
          action: "approve" | "reject";
          contributionIds: Array<Id<"edition_artist_contributions">>;
        },
        any
      >;
      submitArtistContribution: FunctionReference<
        "mutation",
        "public",
        { editionId: number; spotifyId?: string; uniqueArtistId?: string },
        any
      >;
      submitManualArtistContribution: FunctionReference<
        "mutation",
        "public",
        { editionId: number; uniqueArtistId: Id<"unique_artists"> },
        any
      >;
      createPlaceContribution: FunctionReference<
        "mutation",
        "public",
        { editionId: Id<"editions">; placeName: string },
        any
      >;
      renameStage: FunctionReference<
        "mutation",
        "public",
        {
          editionId: Id<"editions">;
          name: string;
          placeId: Id<"places"> | Id<"edition_place_contributions">;
        },
        any
      >;
      deleteStage: FunctionReference<
        "mutation",
        "public",
        {
          editionId: Id<"editions">;
          placeId: Id<"places"> | Id<"edition_place_contributions">;
        },
        any
      >;
      createScheduleContribution: FunctionReference<
        "mutation",
        "public",
        {
          artistIds: Array<
            Id<"edition_artists"> | Id<"edition_artist_contributions">
          >;
          editionId: Id<"editions">;
          endTime?: string;
          placeId?: Id<"places"> | Id<"edition_place_contributions">;
          scheduleId?: number;
          startDate: string;
          startTime?: string;
        },
        any
      >;
      createScheduleAsManager: FunctionReference<
        "mutation",
        "public",
        {
          artistIds: Array<
            Id<"edition_artists"> | Id<"edition_artist_contributions">
          >;
          editionId: Id<"editions">;
          endTime?: string;
          placeId?: Id<"places"> | Id<"edition_place_contributions">;
          scheduleId?: number;
          startDate: string;
          startTime?: string;
        },
        any
      >;
      updateScheduleContribution: FunctionReference<
        "mutation",
        "public",
        {
          artistIds: Array<
            Id<"edition_artists"> | Id<"edition_artist_contributions">
          >;
          contributionId: Id<"edition_schedule_contributions">;
          endTime?: string;
          placeId?: Id<"places"> | Id<"edition_place_contributions">;
          startDate: string;
          startTime?: string;
        },
        any
      >;
      deleteScheduleContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_schedule_contributions"> },
        any
      >;
      approveScheduleContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_schedule_contributions"> },
        any
      >;
      rejectScheduleContribution: FunctionReference<
        "mutation",
        "public",
        { contributionId: Id<"edition_schedule_contributions"> },
        any
      >;
      bulkUpdateScheduleContributions: FunctionReference<
        "mutation",
        "public",
        {
          action: "approve" | "reject";
          contributionIds: Array<Id<"edition_schedule_contributions">>;
        },
        any
      >;
    };
    admin: {
      editionAdmin: {
        updateEditionAdminSettings: FunctionReference<
          "mutation",
          "public",
          { editionId: number; premium?: boolean },
          any
        >;
        updateEditionFeatures: FunctionReference<
          "mutation",
          "public",
          { editionId: number; featureIds: Array<string> },
          any
        >;
        updateEditionLocales: FunctionReference<
          "mutation",
          "public",
          {
            default_locale_id: number;
            editionId: number;
            localeIds: Array<number>;
          },
          any
        >;
      };
      users: {
        createUser: FunctionReference<
          "mutation",
          "public",
          { email: string; roleIds: Array<number> },
          any
        >;
        updateUserRoles: FunctionReference<
          "mutation",
          "public",
          { roleIds: Array<number>; userId: number },
          any
        >;
        setFestivalManager: FunctionReference<
          "mutation",
          "public",
          {
            festUserId: Id<"fest_users">;
            festivalId: Id<"festivals">;
            isManager: boolean;
          },
          { success: true }
        >;
      };
      festivalWidgets: {
        createFestivalWidgetAdmin: FunctionReference<
          "mutation",
          "public",
          { festivalId: number },
          { licenseKey: string }
        >;
        deleteFestivalWidgetAdmin: FunctionReference<
          "mutation",
          "public",
          { festivalId: number },
          any
        >;
      };
      festivalFeatures: {
        updateAllowedOrganizersCount: FunctionReference<
          "mutation",
          "public",
          { count: number; festivalSlug: string },
          any
        >;
        updateFestivalFeatures: FunctionReference<
          "mutation",
          "public",
          {
            features?: {
              activations?: boolean;
              ads_full?: boolean;
              ads_limited?: boolean;
              announcement_open_link?: boolean;
              categories?: boolean;
              custom_icons?: boolean;
              display_artists_by_category?: boolean;
              event_list?: boolean;
              interactive_maps?: boolean;
              multiple_locales?: boolean;
              scheduled_announcements?: boolean;
              sponsor_categories?: boolean;
              stats?: boolean;
            };
            festivalSlug: string;
            maxActivations?: number | null;
          },
          any
        >;
      };
      onboardUser: {
        onboardUser: FunctionReference<
          "mutation",
          "public",
          {
            allowedOrganizersCount: number;
            features: {
              activations: boolean;
              ads_full: boolean;
              ads_limited: boolean;
              announcement_open_link: boolean;
              categories: boolean;
              custom_icons: boolean;
              display_artists_by_category: boolean;
              event_list: boolean;
              interactive_maps: boolean;
              multiple_locales: boolean;
              scheduled_announcements: boolean;
              sponsor_categories: boolean;
              stats: boolean;
            };
            festival:
              | { mode: "existing"; slug: string }
              | { mode: "new"; name: string };
            locale: string;
            maxActivations?: number;
            origin: string;
            userEmail: string;
          },
          {
            festivalName: string;
            festivalSlug: string;
            isNewFestival: boolean;
            isNewUser: boolean;
            userEmail: string;
          }
        >;
      };
      festivals: {
        updateFestival: FunctionReference<
          "mutation",
          "public",
          {
            capacity_range?: string;
            festivalId: Id<"festivals">;
            genres?: Array<{ name: string; slug: string }>;
            searchable?: boolean;
          },
          any
        >;
        deleteFestival: FunctionReference<
          "mutation",
          "public",
          { festivalId: Id<"festivals"> },
          any
        >;
        bulkAction: FunctionReference<
          "mutation",
          "public",
          {
            action: "searchable" | "unsearchable" | "delete";
            festivalIds: Array<Id<"festivals">>;
          },
          any
        >;
        updateWebsiteUrl: FunctionReference<
          "mutation",
          "public",
          { festivalId: Id<"festivals">; url: string },
          any
        >;
        updateTicketUrl: FunctionReference<
          "mutation",
          "public",
          { editionId: Id<"editions">; url: string },
          any
        >;
        updateEditionDates: FunctionReference<
          "mutation",
          "public",
          {
            end_date: string;
            festivalId: Id<"festivals">;
            latestEditionId: Id<"editions">;
            start_date: string;
          },
          any
        >;
      };
      crawlerRuns: {
        approve: FunctionReference<
          "mutation",
          "public",
          {
            crawlerRunId: Id<"crawler_runs">;
            excludedActionIndices?: Array<number>;
          },
          any
        >;
        reject: FunctionReference<
          "mutation",
          "public",
          { crawlerRunId: Id<"crawler_runs">; reason?: string },
          any
        >;
      };
      activationRewards: {
        attachRewardToActivation: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            inventory_total?: number | null;
            order?: number;
            rewardId: Id<"edition_rewards">;
            weight?: number;
          },
          { attachmentId: Id<"activation_rewards"> }
        >;
        updateAttachment: FunctionReference<
          "mutation",
          "public",
          {
            attachmentId: Id<"activation_rewards">;
            inventory_total?: number | null;
            order?: number;
            weight?: number;
          },
          { success: true }
        >;
        detachRewardFromActivation: FunctionReference<
          "mutation",
          "public",
          { attachmentId: Id<"activation_rewards"> },
          { success: true }
        >;
        reorderAttachments: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            orderedIds: Array<Id<"activation_rewards">>;
          },
          { success: true }
        >;
        bulkUploadUniqueCodes: FunctionReference<
          "mutation",
          "public",
          { attachmentId: Id<"activation_rewards">; codes: Array<string> },
          { inserted: number; skipped_duplicates: number }
        >;
        deleteUnclaimedCodes: FunctionReference<
          "mutation",
          "public",
          { attachmentId: Id<"activation_rewards"> },
          { deleted: number }
        >;
      };
      activations: {
        createActivation: FunctionReference<
          "mutation",
          "public",
          {
            badge_color?: string;
            badge_file_id?: number | Id<"files">;
            badge_icon: string;
            can_win_multiple_rewards?: boolean;
            cooldown_seconds_per_user?: number;
            editionId: number;
            end_at: number;
            hidden?: boolean;
            max_attempts_per_user?: number;
            order?: number;
            sponsor_id?: Id<"sponsors">;
            start_at: number;
            type: "form" | "quiz" | "scratch" | "wheel";
          },
          { activationId: Id<"activations"> }
        >;
        updateActivation: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            badge_color?: string | null;
            badge_file_id?: number | Id<"files"> | null;
            badge_icon?: string;
            can_win_multiple_rewards?: boolean;
            config?:
              | { type: "form" }
              | { pass_threshold?: number; type: "quiz" }
              | {
                  cover_file_id?: number | Id<"files">;
                  type: "scratch";
                  winning_symbol_file_id?: number | Id<"files">;
                }
              | { type: "wheel" };
            cooldown_seconds_per_user?: number;
            end_at?: number;
            hero_file_id?: number | Id<"files"> | null;
            hidden?: boolean;
            max_attempts_per_user?: number;
            order?: number;
            sponsor_id?: Id<"sponsors"> | null;
            start_at?: number;
          },
          { success: true }
        >;
        setActivationPublishedAt: FunctionReference<
          "mutation",
          "public",
          { activationId: Id<"activations">; publishedAt: number | null },
          { success: true }
        >;
        deleteActivation: FunctionReference<
          "mutation",
          "public",
          { activationId: Id<"activations"> },
          { deleted_submissions: number; success: true }
        >;
        upsertActivationLocalized: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            field: string;
            locale: string;
            value: string;
          },
          { success: true }
        >;
        upsertActivationQuestion: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            kind: "text" | "number" | "single_choice" | "multi_choice";
            order?: number;
            questionId?: Id<"activation_questions">;
            required: boolean;
          },
          { questionId: Id<"activation_questions"> }
        >;
        deleteActivationQuestion: FunctionReference<
          "mutation",
          "public",
          { questionId: Id<"activation_questions"> },
          { success: true }
        >;
        reorderActivationQuestions: FunctionReference<
          "mutation",
          "public",
          {
            activationId: Id<"activations">;
            orderedIds: Array<Id<"activation_questions">>;
          },
          { success: true }
        >;
        upsertActivationQuestionLocalized: FunctionReference<
          "mutation",
          "public",
          {
            field: string;
            locale: string;
            questionId: Id<"activation_questions">;
            value: string;
          },
          { success: true }
        >;
        upsertActivationQuestionOption: FunctionReference<
          "mutation",
          "public",
          {
            optionId?: Id<"activation_question_options">;
            order?: number;
            questionId: Id<"activation_questions">;
          },
          { optionId: Id<"activation_question_options"> }
        >;
        deleteActivationQuestionOption: FunctionReference<
          "mutation",
          "public",
          { optionId: Id<"activation_question_options"> },
          { success: true }
        >;
        upsertActivationQuestionOptionLocalized: FunctionReference<
          "mutation",
          "public",
          {
            field: string;
            locale: string;
            optionId: Id<"activation_question_options">;
            value: string;
          },
          { success: true }
        >;
        setQuestionCorrectOptions: FunctionReference<
          "mutation",
          "public",
          {
            optionIds: Array<Id<"activation_question_options">>;
            questionId: Id<"activation_questions">;
          },
          { success: true }
        >;
      };
      editionRewards: {
        createEditionReward: FunctionReference<
          "mutation",
          "public",
          {
            color?: string | null;
            cta_url?: string | null;
            display_type: "code" | "qr" | "external_url" | "email_followup";
            editionId: number | Id<"editions">;
            icon_name?: string | null;
            image_file_id?: number | Id<"files"> | null;
            inventory_kind: "unique_pool" | "shared" | "none";
            redeem_by_at?: number | null;
            shared_code?: string | null;
          },
          { rewardId: Id<"edition_rewards"> }
        >;
        updateEditionReward: FunctionReference<
          "mutation",
          "public",
          {
            color?: string | null;
            cta_url?: string | null;
            display_type?: "code" | "qr" | "external_url" | "email_followup";
            icon_name?: string | null;
            image_file_id?: number | Id<"files"> | null;
            inventory_kind?: "unique_pool" | "shared" | "none";
            redeem_by_at?: number | null;
            rewardId: Id<"edition_rewards">;
            shared_code?: string | null;
          },
          { success: true }
        >;
        deleteEditionReward: FunctionReference<
          "mutation",
          "public",
          { rewardId: Id<"edition_rewards"> },
          { success: true }
        >;
        upsertEditionRewardLocalized: FunctionReference<
          "mutation",
          "public",
          {
            field: string;
            locale: string;
            rewardId: Id<"edition_rewards">;
            value: string;
          },
          { success: true }
        >;
      };
    };
    address: {
      upsertAddress: FunctionReference<
        "mutation",
        "public",
        {
          city: string;
          country_id: Id<"country">;
          full_address: string;
          google_id: string;
          latitude: number;
          longitude: number;
          region_name?: string;
        },
        any
      >;
    };
    import: {
      importSponsors: FunctionReference<
        "mutation",
        "public",
        {
          currentEditionId: number;
          sourceEditionId: number;
          sponsorIds: Array<number>;
        },
        { importedCount: number; success: boolean }
      >;
      importFaqItems: FunctionReference<
        "mutation",
        "public",
        {
          currentEditionId: number;
          itemIds: Array<number>;
          sourceEditionId: number;
        },
        { importedItemsCount: number; success: boolean }
      >;
      importPlaces: FunctionReference<
        "mutation",
        "public",
        {
          currentEditionId: number;
          placeIds: Array<number>;
          sourceEditionId: number;
        },
        { importedCount: number; success: boolean }
      >;
      importMapData: FunctionReference<
        "mutation",
        "public",
        {
          currentEditionId: number;
          pinIds: Array<number>;
          polygonIds: Array<number>;
          sourceEditionId: number;
        },
        {
          importedIconsCount: number;
          importedPinsCount: number;
          importedPlaceTypesCount: number;
          importedPlacesCount: number;
          importedPolygonsCount: number;
          success: boolean;
        }
      >;
    };
    festivals: {
      createFestival: FunctionReference<
        "mutation",
        "public",
        { name: string },
        { festivalId: number; slug: string }
      >;
    };
    spotify: {
      connectSpotify: FunctionReference<
        "mutation",
        "public",
        {
          accessToken: string;
          expiresIn: number;
          refreshToken: string;
          spotifyUserId: string;
        },
        any
      >;
      disconnectSpotify: FunctionReference<
        "mutation",
        "public",
        Record<string, never>,
        any
      >;
      updateSpotifyToken: FunctionReference<
        "mutation",
        "public",
        { accessToken: string; expiresIn: number; refreshToken?: string },
        any
      >;
    };
    editionAssets: {
      storeEditionAssets: FunctionReference<
        "mutation",
        "public",
        {
          assets: Array<{
            category: string;
            expiresAt: number;
            height: number;
            name: string;
            templateId: string;
            url: string;
            width: number;
          }>;
          editionId: number;
        },
        { success: boolean }
      >;
      replaceEditionAssets: FunctionReference<
        "mutation",
        "public",
        {
          assets: Array<{
            category: string;
            expiresAt: number;
            height: number;
            name: string;
            templateId: string;
            url: string;
            width: number;
          }>;
          editionId: number;
        },
        { success: boolean }
      >;
      replaceCategoryAssets: FunctionReference<
        "mutation",
        "public",
        {
          assets: Array<{
            category: string;
            expiresAt: number;
            height: number;
            name: string;
            templateId: string;
            url: string;
            width: number;
          }>;
          category: string;
          editionId: number;
        },
        { success: boolean }
      >;
      setEditionOverrides: FunctionReference<
        "mutation",
        "public",
        { editionId: number; overrides: any },
        { success: boolean }
      >;
      saveAsset: FunctionReference<
        "mutation",
        "public",
        {
          category: string;
          editionId: number;
          expiresAt: number;
          height: number;
          name: string;
          source: string;
          templateId: string;
          url: string;
          variables: any;
          width: number;
        },
        Id<"edition_assets">
      >;
      updateAsset: FunctionReference<
        "mutation",
        "public",
        {
          expiresAt?: number;
          id: Id<"edition_assets">;
          name?: string;
          url?: string;
          variables?: any;
        },
        { success: boolean }
      >;
      deleteAsset: FunctionReference<
        "mutation",
        "public",
        { id: Id<"edition_assets"> },
        { success: boolean }
      >;
    };
    onboarding: {
      onboardEdition: FunctionReference<
        "mutation",
        "public",
        {
          address_id: Id<"address">;
          aftermovie_link?: string;
          cashless_link?: string;
          country_id: string;
          cover_background_id: number;
          cover_logo_id?: number;
          default_locale_id: number;
          end_date: string;
          existingEditionId?: Id<"editions">;
          festivalSlug: string;
          hide_cover_logo: boolean;
          home_background_shade_percentage: number;
          locale_ids: Array<number>;
          merch_link?: string;
          primary_color: string;
          social_link_urls?: Array<string>;
          start_date: string;
          ticket_link?: string;
          timezone_offset_minutes: number;
        },
        { editionSlug: string }
      >;
    };
    seedAssetsTemplates: {
      seedDefaultTemplates: FunctionReference<
        "mutation",
        "public",
        Record<string, never>,
        { created: number; message: string; updated: number }
      >;
    };
    activations: {
      submit: FunctionReference<
        "mutation",
        "public",
        {
          activationId: Id<"activations">;
          client_request_id: string;
          result:
            | {
                answers: Array<{
                  option_ids?: Array<Id<"activation_question_options">>;
                  question_id: Id<"activation_questions">;
                  value?: string;
                }>;
                type: "form";
              }
            | {
                answers: Array<{
                  option_ids: Array<Id<"activation_question_options">>;
                  question_id: Id<"activation_questions">;
                }>;
                type: "quiz";
              }
            | { type: "scratch" }
            | { type: "wheel" };
        },
        {
          award: null | {
            _id: Id<"activation_reward_awards">;
            activation_reward_id: Id<"activation_rewards">;
            code: string | null;
            color: string | null;
            cta_label: string | null;
            cta_url: string | null;
            description: string | null;
            display_type: "code" | "qr" | "external_url" | "email_followup";
            expires_at: number | null;
            icon_name: string | null;
            image_file_id: number | Id<"files"> | null;
            image_url: string | null;
            inventory_kind: "unique_pool" | "shared" | "none";
            redemption_instructions: string | null;
            reward_id: Id<"edition_rewards">;
            terms: string | null;
            title: string | null;
          };
          layout: null | Array<number>;
          ok: true;
          submissionId: Id<"activation_submissions">;
        }
      >;
    };
    backstage: {
      import: {
        upsertConnection: FunctionReference<
          "mutation",
          "public",
          {
            config: any;
            editionId: number;
            provider: "festigest" | "zonefestival";
          },
          { ok: boolean }
        >;
        upsertMapping: FunctionReference<
          "mutation",
          "public",
          {
            editionId: number;
            mapping: any;
            provider: "festigest" | "zonefestival";
          },
          { ok: boolean }
        >;
        copyMappingFromEdition: FunctionReference<
          "mutation",
          "public",
          {
            editionId: number;
            provider: "festigest" | "zonefestival";
            sourceEditionId: number;
          },
          { copied: boolean; ok: boolean }
        >;
        startRun: FunctionReference<
          "mutation",
          "public",
          {
            editionId: number;
            provider: "festigest" | "zonefestival";
            restrictToEditionDates?: boolean;
          },
          { runId: Id<"import_runs"> }
        >;
        confirmRun: FunctionReference<
          "mutation",
          "public",
          { runId: Id<"import_runs"> },
          { ok: boolean }
        >;
        fetchSourceSchema: FunctionReference<
          "action",
          "public",
          { editionId: number; provider: "festigest" | "zonefestival" },
          any
        >;
        testConnection: FunctionReference<
          "action",
          "public",
          {
            config: any;
            editionId: number;
            provider: "festigest" | "zonefestival";
          },
          any
        >;
      };
    };
  };
  firebase: {
    sendAppMagicLink: FunctionReference<
      "action",
      "public",
      { email: string; locale: string },
      any
    >;
    sendBackstageMagicLink: FunctionReference<
      "action",
      "public",
      { email: string; locale: string; origin: string; redirectUrl: string },
      any
    >;
    sendMagicLink: FunctionReference<
      "action",
      "public",
      { email: string; locale: string; origin: string; redirectUrl: string },
      any
    >;
    syncUser: FunctionReference<
      "action",
      "public",
      { appVersion?: string; idToken: string },
      any
    >;
  };
  r2: {
    generateUploadUrl: FunctionReference<
      "mutation",
      "public",
      Record<string, never>,
      { key: string; url: string }
    >;
    generateUsersStorageUploadUrl: FunctionReference<
      "mutation",
      "public",
      Record<string, never>,
      { key: string; url: string }
    >;
    syncMetadata: FunctionReference<
      "mutation",
      "public",
      { key: string },
      null
    >;
    syncUsersStorageMetadata: FunctionReference<
      "mutation",
      "public",
      { key: string },
      null
    >;
  };
  _app: {
    editionAdsBanners: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions">; supportsTargeting?: boolean },
        Array<{
          _id: Id<"editions_ads_banner">;
          images: Array<{ formats?: any; locale: string; url: string }>;
          links: Array<{ link_url: string; locale: string }>;
          placements: Array<{
            _id: Id<"editions_ads_placement">;
            slug: string;
            target_ids: Array<Id<"edition_artists"> | Id<"edition_events">>;
            weight: number;
          }>;
        }>
      >;
      recordBannerView: FunctionReference<
        "mutation",
        "public",
        {
          placementId: Id<"editions_ads_placement">;
          userCountry?: string;
          userId?: Id<"fest_users">;
          userLocale?: string;
          userOs?: string;
        },
        { success: boolean }
      >;
      recordBannerClick: FunctionReference<
        "mutation",
        "public",
        {
          placementId: Id<"editions_ads_placement">;
          userCountry?: string;
          userId?: Id<"fest_users">;
          userLocale?: string;
          userOs?: string;
        },
        { success: boolean }
      >;
    };
    editionAnnouncements: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"announcements">;
          deep_link?: string;
          link?: { url: string };
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          pinned: boolean;
          published_at: string;
          updated_at: string;
        }>
      >;
    };
    editionArtists: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_artists">;
          categories: Array<{
            _id: Id<"edition_artists_categories">;
            home_preview?: boolean;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          custom_fields: Array<{
            _id: Id<"custom_fields">;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          image: null | { formats?: any; url: string };
          links: Array<{ url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          name: string;
          order?: number;
          unique_artist: null | {
            _id: Id<"unique_artists">;
            image?: { formats?: any; url: string };
            name: string;
            preview_title?: string;
            preview_url?: string;
            slug?: string;
            spotify_popularity?: number;
          };
          youtube_video_link?: string;
        }>
      >;
    };
    editionEvents: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_events">;
          artists: Array<Id<"edition_artists">>;
          artistsOrder: Array<{ id: Id<"edition_artists">; order?: number }>;
          categories: Array<{
            _id: Id<"events_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          custom_fields: Array<{
            _id: Id<"custom_fields">;
            localized_fields: Array<{
              label: string;
              locale: string;
              value: string;
            }>;
          }>;
          image?: { formats?: any; url: string };
          links: Array<{ url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
        }>
      >;
    };
    editionFaq: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"faq_categories">;
          faq_items: Array<{
            _id: Id<"faq_items">;
            link?: { url: string };
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          order?: number;
        }>
      >;
    };
    editionPlaces: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"places">;
          image?: { formats?: any; url: string };
          links: Array<{ url: string }>;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          maps_pin_id?: Id<"maps_pins">;
          order?: number;
          place_type_id?: Id<"place_types">;
        }>
      >;
    };
    editionPlacesTypes: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"place_types">;
          home_preview: boolean;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          order?: number;
          show_in_list: boolean;
        }>
      >;
    };
    editionSchedules: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"edition_schedules">;
          artist?: {
            _id: Id<"edition_artists">;
            name: string;
            unique_artist_id?: Id<"unique_artists">;
          };
          categories: Array<{
            _id: Id<"edition_schedules_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          }>;
          end_date?: string;
          end_time?: string;
          event?: {
            _id: Id<"edition_events">;
            artists: Array<Id<"edition_artists">>;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            unique_artist_ids: Array<Id<"unique_artists">>;
          };
          place?: {
            _id: Id<"places">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          };
          start_date: string;
          start_time?: string;
          ticket_url?: string;
        }>
      >;
    };
    editionSponsors: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          _id: Id<"sponsors">;
          link?: { url: string };
          logo?: { formats?: any; url: string };
          name: string;
          order?: number;
        }>
      >;
      byEditionIdGroupedBySponsors: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        Array<{
          category: {
            _id: Id<"sponsor_categories">;
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            order?: number;
          } | null;
          sponsors: Array<{
            _id: Id<"sponsors">;
            dark_logo?: { formats?: any; url: string };
            link?: { url: string };
            logo?: { formats?: any; url: string };
            name: string;
            order?: number;
          }>;
        }>
      >;
    };
    editions: {
      bySlug: FunctionReference<
        "query",
        "public",
        { editionSlug: string; festivalSlug: string },
        null | Id<"editions">
      >;
      byId: FunctionReference<
        "query",
        "public",
        { id: Id<"editions"> },
        null | {
          _id: Id<"editions">;
          address: { city: string; country: string; full_address: string };
          after_movie_link?: { url: string };
          bottom_bar_config?: any;
          bottom_bar_mode?: "automatic" | "tickets" | "cashless" | "custom";
          cashless_link?: { url: string };
          contact_link?: { url: string };
          cover_background: { formats?: any; url: string };
          cover_logo?: { formats?: any; url: string };
          custom_link?: { url: string };
          day_start_time: string;
          default_locale_code: string;
          design: {
            colors: {
              primary: string;
              primaryAltOne: string;
              primaryAltOneForeground: string;
              primaryAltThree: string;
              primaryAltThreeForeground: string;
              primaryAltTwo: string;
              primaryAltTwoForeground: string;
              primaryForeground: string;
            };
            home_background_shade_percentage: number;
          };
          display_artists_by_category: boolean;
          end_date: string;
          festival: {
            _id: Id<"festivals">;
            kind: string;
            name: string;
            slug: string;
          };
          festival_editions: Array<{ _id: Id<"editions">; start_date: string }>;
          managed: boolean;
          map_images: Array<{ formats?: any; url: string }>;
          merchandising_link?: { url: string };
          min_app_version: string;
          playlist_url?: string;
          premium: boolean;
          show_artists: boolean;
          show_event_times: boolean;
          show_events: boolean;
          show_stages: boolean;
          slug: string;
          social_links: Array<{ url: string }>;
          start_date: string;
          ticket_link?: { url: string };
          timezone_offset_minutes: number;
          website_link?: { url: string };
          year: number;
        }
      >;
      trending: FunctionReference<
        "query",
        "public",
        {
          countryCode?: string;
          location?: { latitude: number; longitude: number };
        },
        Array<{
          _id: Id<"editions">;
          address: {
            city: string;
            country_code: string;
            country_name: string;
            full_address: string;
          };
          cover_background?: { formats?: any; url: string };
          end_date: string;
          festival: { _id: Id<"festivals">; name: string };
          min_app_version: string;
          premium: boolean;
          start_date: string;
          year: number;
        }>
      >;
      upcoming: FunctionReference<
        "query",
        "public",
        {
          countryCode?: string;
          location?: { latitude: number; longitude: number };
        },
        Array<{
          _id: Id<"editions">;
          address: {
            city: string;
            country_code: string;
            country_name: string;
            full_address: string;
          };
          cover_background?: { formats?: any; url: string };
          end_date: string;
          festival: { _id: Id<"festivals">; name: string };
          min_app_version: string;
          premium: boolean;
          start_date: string;
          year: number;
        }>
      >;
      explore: FunctionReference<
        "query",
        "public",
        {
          countryCode?: string;
          date?: string;
          dateRange?: { endDate: string; startDate: string };
          location?: { latitude: number; longitude: number };
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
        },
        {
          continueCursor: string;
          isDone: boolean;
          page: Array<{
            _id: Id<"editions">;
            address: {
              city: string;
              country_code: string;
              country_name: string;
              full_address: string;
            };
            cover_background: { formats?: any; url: string };
            end_date: string;
            festival: { _id: Id<"festivals">; name: string };
            min_app_version: string;
            premium: boolean;
            start_date: string;
            year: number;
          }>;
        }
      >;
      legacyIdToId: FunctionReference<
        "query",
        "public",
        { legacyId: number },
        null | Id<"editions">
      >;
    };
    editionMap: {
      byEditionId: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        null | {
          _id: Id<"maps">;
          default_zoom: number;
          latitude: number;
          longitude: number;
          maps_pins: Array<{
            _id: Id<"maps_pins">;
            icon?: { _id: Id<"files">; formats?: any; url: string };
            icon_z_offset?: number;
            latitude: number;
            longitude: number;
            name: string;
            place_id?: Id<"places">;
            place_type_id?: Id<"place_types">;
            show_name?: boolean;
            size_modifier?: number;
          }>;
          maps_polygons: Array<{
            _id: Id<"maps_polygons">;
            color?: string;
            coordinates: any;
            extrusion_height?: number;
            extrusion_start?: number;
            pin_id?: Id<"maps_pins">;
            zone_order?: number;
          }>;
          preview_image?: { formats?: any; url: string };
          style_url: string;
        }
      >;
    };
    location: {
      search: FunctionReference<
        "action",
        "public",
        { query: string; sessionId: string },
        Array<{ id: string; name: string }>
      >;
      details: FunctionReference<
        "action",
        "public",
        { id: string; sessionId: string },
        {
          id: string;
          location: { latitude: number; longitude: number };
          name: string;
        }
      >;
    };
    spotify: {
      getSpotifyStatus: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        null | {
          connectedAt: string;
          isConnected: boolean;
          isTokenExpired: boolean;
          lastSyncedAt?: string;
          spotifyUserId: string;
        }
      >;
      getFollowedArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"spotify_followed_artists">;
          followers?: number;
          genres?: Array<string>;
          imageUrl?: string;
          name: string;
          popularity?: number;
          spotifyArtistId: string;
          syncedAt: string;
          uniqueArtistId?: Id<"unique_artists">;
        }>
      >;
      getMatchedFollowedArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{ _id: Id<"unique_artists">; imageUrl?: string; name: string }>
      >;
    };
    users: {
      me: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        {
          _id: Id<"fest_users">;
          created_at: string;
          email: string;
          first_name?: string;
          last_name?: string;
          username?: string;
        }
      >;
      setLocale: FunctionReference<
        "mutation",
        "public",
        { locale: string },
        { success: boolean }
      >;
      setNotificationToken: FunctionReference<
        "mutation",
        "public",
        { notificationToken: string },
        { success: boolean }
      >;
      setNotificationSettings: FunctionReference<
        "mutation",
        "public",
        {
          artistsAnnouncements: boolean;
          festAnnouncements: boolean;
          festivalAnnouncements: boolean;
          festivalSchedulesReminders: boolean;
        },
        { success: boolean }
      >;
      getEmailPreferences: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          category: string;
          description: string;
          label: string;
          subscribed: boolean;
        }>
      >;
      setEmailSubscription: FunctionReference<
        "mutation",
        "public",
        { category: string; subscribed: boolean },
        { success: boolean }
      >;
      replaceMutedItems: FunctionReference<
        "mutation",
        "public",
        {
          mutedArtists: Array<Id<"unique_artists">>;
          mutedFestivals: Array<Id<"festivals">>;
        },
        { success: boolean }
      >;
      getMutedFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<Id<"festivals">>
      >;
      getMutedArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<Id<"unique_artists">>
      >;
      deleteAccount: FunctionReference<
        "mutation",
        "public",
        Record<string, never>,
        { success: boolean }
      >;
    };
    usersFollows: {
      followedFestivals: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"festivals">;
          latest_edition: {
            _id: Id<"editions">;
            cover_background: { formats?: any; url: string };
            end_date: string;
            min_app_version: string;
            start_date: string;
            timezone_offset_minutes: number;
          };
          name: string;
        }>
      >;
      goingEditions: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"festivals">;
          edition: {
            _id: Id<"editions">;
            address?: string;
            cover_background: { formats?: any; url: string };
            day_start_time: string;
            end_date: string;
            min_app_version: string;
            start_date: string;
            timezone_offset_minutes: number;
          };
          name: string;
        }>
      >;
      replaceAll: FunctionReference<
        "mutation",
        "public",
        {
          followedArtists: Array<Id<"unique_artists">>;
          followedEditionArtists: Array<Id<"edition_artists">>;
          followedEditionEvents: Array<Id<"edition_events">>;
          followedFestivals: Array<Id<"festivals">>;
          goingToEditions: Array<Id<"editions">>;
        },
        { success: boolean }
      >;
      migrateAll: FunctionReference<
        "mutation",
        "public",
        {
          followedArtists: Array<number>;
          followedEditionArtists: Array<number>;
          followedFestivals: Array<number>;
        },
        { success: boolean }
      >;
      goingToEditions: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<Id<"editions">>
      >;
      followedArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"unique_artists">;
          image?: { formats?: any; url: string };
          name: string;
        }>
      >;
      followedEditionArtists: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"edition_artists">;
          edition: {
            _id: Id<"editions">;
            default_locale_code: string;
            end_date: string;
            festival: { _id: Id<"festivals">; name: string };
            start_date: string;
            timezone_offset_minutes: number;
          };
          name: string;
          schedules: Array<{
            _id: Id<"edition_schedules">;
            artist?: { name: string };
            end_date?: string;
            end_time?: string;
            event?: {
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            place?: {
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            start_date: string;
            start_time?: string;
          }>;
        }>
      >;
      followedEditionEvents: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"edition_events">;
          edition: {
            _id: Id<"editions">;
            default_locale_code: string;
            end_date: string;
            festival: { _id: Id<"festivals">; name: string };
            start_date: string;
            timezone_offset_minutes: number;
          };
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          schedules: Array<{
            _id: Id<"edition_schedules">;
            end_date?: string;
            end_time?: string;
            event?: {
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            place?: {
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
            };
            start_date: string;
            start_time?: string;
          }>;
        }>
      >;
    };
    artists: {
      byId: FunctionReference<
        "query",
        "public",
        { id: Id<"unique_artists"> },
        null | {
          _id: Id<"unique_artists">;
          image?: { formats?: any; url: string };
          name: string;
          preview_title?: string;
          preview_url?: string;
        }
      >;
      artistEditions: FunctionReference<
        "query",
        "public",
        { id: Id<"unique_artists"> },
        Array<{
          _id: Id<"editions">;
          address: {
            city: string;
            country_code: string;
            country_name: string;
            full_address: string;
          };
          cover_background?: { formats?: any; url: string };
          end_date: string;
          festival: { _id: Id<"festivals">; name: string };
          min_app_version: string;
          premium: boolean;
          start_date: string;
          timezone_offset_minutes: number;
          year: number;
        }>
      >;
      trending: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"unique_artists">;
          image?: { formats?: any; url: string };
          name: string;
        }>
      >;
    };
    vitals: {
      versions: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        {
          mandatory_min_app_version: string;
          recommended_min_app_version: string;
        }
      >;
    };
    festival: {
      getLatestPublishedEditionId: FunctionReference<
        "query",
        "public",
        { festivalSlug: string },
        null | Id<"editions">
      >;
    };
    notifications: {
      getUserNotifications: FunctionReference<
        "query",
        "public",
        { cursor?: string; limit?: number },
        {
          nextCursor?: string;
          notifications: Array<{
            _id: Id<"fest_users_notifications">;
            artist_name?: string;
            body: string;
            created_at: string;
            deep_link?: string;
            edition_id?: Id<"editions">;
            edition_year?: number;
            festival_name?: string;
            read_at?: string;
            title: string;
            type: "artist_announcement" | "cashless_refund_open";
            unique_artist_id?: Id<"unique_artists">;
          }>;
        }
      >;
      getUnreadCount: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        number
      >;
      markNotificationRead: FunctionReference<
        "mutation",
        "public",
        { notificationId: Id<"fest_users_notifications"> },
        { success: boolean }
      >;
      markAllNotificationsRead: FunctionReference<
        "mutation",
        "public",
        Record<string, never>,
        { count: number }
      >;
    };
    tickets: {
      upsert: FunctionReference<
        "mutation",
        "public",
        {
          barcode: { format: string; payload: string };
          clientId: string;
          createdAt: number;
          editionId: Id<"editions">;
          fileName: string;
          fileSize: number;
          fileType: "pdf" | "image";
          parsedText?: string;
          payloadHash: string;
          r2Key: string;
          verified: "match" | "unverified";
        },
        | { _id: Id<"fest_user_tickets">; ok: true }
        | { ok: false; reason: "duplicate" }
      >;
      remove: FunctionReference<
        "mutation",
        "public",
        { clientId: string },
        { success: boolean }
      >;
      listMine: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          _id: Id<"fest_user_tickets">;
          barcode: { format: string; payload: string };
          clientId: string;
          createdAt: number;
          editionId: Id<"editions">;
          fileName: string;
          fileSize: number;
          fileType: "pdf" | "image";
          metadata?: {
            confidence?: number;
            currency?: string;
            event_end_date?: string;
            event_name?: string;
            event_start_date?: string;
            holder_name?: string;
            language?: string;
            price?: number;
            seat?: string;
            summary?: string;
            ticket_days?: Array<string>;
            ticket_type?: string;
            zone?: string;
          };
          metadataError?: string;
          metadataExtractedAt?: number;
          metadataStatus?: "pending" | "processing" | "done" | "failed";
          parsedText?: string;
          payloadHash: string;
          r2Key: string;
          verified: "match" | "unverified";
        }>
      >;
      getFileUrl: FunctionReference<
        "query",
        "public",
        { clientId: string },
        string
      >;
    };
    editionActivations: {
      current: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        {
          _id: Id<"activations">;
          badge_color: string | null;
          badge_icon: string;
          badge_image_url: string | null;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          type: "form" | "quiz" | "scratch" | "wheel";
        } | null
      >;
      byId: FunctionReference<
        "query",
        "public",
        { activationId: Id<"activations"> },
        {
          _id: Id<"activations">;
          badge_color: string | null;
          badge_icon: string;
          can_win_multiple_rewards: boolean;
          config:
            | { type: "form" }
            | { pass_threshold?: number; type: "quiz" }
            | {
                cover_file_id?: number | Id<"files">;
                cover_url?: string;
                type: "scratch";
                winning_symbol_file_id?: number | Id<"files">;
                winning_symbol_url?: string;
              }
            | { type: "wheel" };
          cooldown_seconds_per_user: number;
          default_locale: string;
          edition_id: Id<"editions">;
          end_at: number;
          hero_url: string | null;
          localized_fields: Array<{
            field: string;
            locale: string;
            value: string;
          }>;
          max_attempts_per_user: number;
          questions: Array<{
            _id: Id<"activation_questions">;
            kind: "text" | "number" | "single_choice" | "multi_choice";
            localized_fields: Array<{
              field: string;
              locale: string;
              value: string;
            }>;
            options: Array<{
              _id: Id<"activation_question_options">;
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
              order: number;
            }>;
            order: number;
            required: boolean;
          }>;
          rewards: Array<{
            attachment: {
              _id: Id<"activation_rewards">;
              inventory_claimed: number;
              inventory_total: number | null;
              order: number;
              sold_out: boolean;
              weight: number;
            };
            reward: {
              _id: Id<"edition_rewards">;
              color: string | null;
              cta_url: string | null;
              display_type: "code" | "qr" | "external_url" | "email_followup";
              icon_name: string | null;
              image_file_id: number | Id<"files"> | null;
              image_url: string | null;
              inventory_kind: "unique_pool" | "shared" | "none";
              localized_fields: Array<{
                field: string;
                locale: string;
                value: string;
              }>;
              redeem_by_at: number | null;
              shared_code: string | null;
            };
          }>;
          sponsor: {
            _id: Id<"sponsors">;
            dark_logo_url: string | null;
            link_url: string | null;
            logo_url: string | null;
            name: string;
          } | null;
          start_at: number;
          type: "form" | "quiz" | "scratch" | "wheel";
        } | null
      >;
      mySubmission: FunctionReference<
        "query",
        "public",
        { activationId: Id<"activations"> },
        {
          result:
            | {
                answers: Array<{
                  option_ids?: Array<Id<"activation_question_options">>;
                  question_id: Id<"activation_questions">;
                  value?: string;
                }>;
                type: "form";
              }
            | {
                answers: Array<{
                  option_ids: Array<Id<"activation_question_options">>;
                  question_id: Id<"activation_questions">;
                }>;
                score: number;
                type: "quiz";
              }
            | { layout: Array<number>; type: "scratch" }
            | { type: "wheel" };
          submitted_at: number;
        } | null
      >;
      myActivationState: FunctionReference<
        "query",
        "public",
        { activationId: Id<"activations"> },
        {
          attempts_remaining: number;
          attempts_used: number;
          awards: Array<{
            _id: Id<"activation_reward_awards">;
            activation_reward_id: Id<"activation_rewards">;
            code: string | null;
            created_at: number;
            display_type: "code" | "qr" | "external_url" | "email_followup";
            expires_at: number | null;
            inventory_kind: "unique_pool" | "shared" | "none";
            reward_id: Id<"edition_rewards">;
          }>;
          can_play: boolean;
          cooldown_until: number | null;
          has_won: boolean;
          last_award: {
            _id: Id<"activation_reward_awards">;
            activation_reward_id: Id<"activation_rewards">;
            code: string | null;
            created_at: number;
            display_type: "code" | "qr" | "external_url" | "email_followup";
            expires_at: number | null;
            inventory_kind: "unique_pool" | "shared" | "none";
            reward_id: Id<"edition_rewards">;
          } | null;
        }
      >;
    };
    cashless: {
      getEditionConfig: FunctionReference<
        "query",
        "public",
        { editionId: Id<"editions"> },
        null | {
          configId: Id<"edition_cashless_configs">;
          provider: string;
          refund?: {
            end: string;
            isAutomatic: boolean;
            start: string;
            url?: string;
          };
        }
      >;
      getConfig: FunctionReference<
        "action",
        "public",
        { configId: Id<"edition_cashless_configs"> },
        {
          accountEnabled: boolean;
          capabilities: { balance: boolean; paymentMode: "hosted_url" };
          currency: { decimals: number; symbol: string };
          customAmount: { allowed: boolean; max?: number; min?: number };
          fastTopUpOpen: boolean;
          isOpen: boolean;
          locales: Array<string>;
          presetAmounts: Array<number>;
          provider: string;
          tagFormat: {
            label: "wristband" | "card";
            maxLength: number;
            minLength: number;
          };
          tosUrl?: string;
        }
      >;
      validateTag: FunctionReference<
        "action",
        "public",
        { code: string; configId: Id<"edition_cashless_configs"> },
        {
          authorized: boolean;
          holderFirstName?: string;
          requiresUnsupportedStep: boolean;
          window?: { end: string; start: string };
        }
      >;
      createTopUp: FunctionReference<
        "action",
        "public",
        {
          amountCents: number;
          clientRef: string;
          code: string;
          configId: Id<"edition_cashless_configs">;
          locale: string;
        },
        {
          currentBalance?: number;
          expiresAt?: string;
          paymentUrl: string;
          topUpId: Id<"cashless_topups">;
        }
      >;
      getTopUpStatus: FunctionReference<
        "action",
        "public",
        { topUpId: Id<"cashless_topups"> },
        {
          currentBalance?: number;
          status:
            | "pending"
            | "succeeded"
            | "failed"
            | "cancelled"
            | "expired"
            | "unknown";
        }
      >;
      reportClientOutcome: FunctionReference<
        "mutation",
        "public",
        { outcome: "cancelled" | "expired"; topUpId: Id<"cashless_topups"> },
        null
      >;
      getAccountStatus: FunctionReference<
        "query",
        "public",
        { configId: Id<"edition_cashless_configs"> },
        null | { email: string }
      >;
      checkAccountEmail: FunctionReference<
        "action",
        "public",
        { configId: Id<"edition_cashless_configs">; email: string },
        { exists: boolean; firstName?: string }
      >;
      linkAccount: FunctionReference<
        "action",
        "public",
        {
          configId: Id<"edition_cashless_configs">;
          email: string;
          password: string;
        },
        { email: string }
      >;
      registerAccount: FunctionReference<
        "action",
        "public",
        {
          acceptOrgaContact: boolean;
          birthdate: string;
          configId: Id<"edition_cashless_configs">;
          email: string;
          firstName: string;
          lastName: string;
          locale: string;
          password: string;
        },
        { email: string }
      >;
      requestPasswordReset: FunctionReference<
        "action",
        "public",
        { configId: Id<"edition_cashless_configs">; email: string },
        null
      >;
      unlinkAccount: FunctionReference<
        "mutation",
        "public",
        { configId: Id<"edition_cashless_configs"> },
        null
      >;
      getAccount: FunctionReference<
        "action",
        "public",
        { configId: Id<"edition_cashless_configs"> },
        {
          email: string;
          wallets: Array<{
            balance?: { amount: number; decimals: number; symbol: string };
            canTopUp: boolean;
            name?: string;
            refund?: {
              canAskRefund: boolean;
              end?: string;
              isAuthorized: boolean;
              isAutomatic: boolean;
              start?: string;
            };
            shortTag?: string;
            walletId: string;
          }>;
        }
      >;
      addAccountWallet: FunctionReference<
        "action",
        "public",
        {
          birthdate: string;
          code: string;
          configId: Id<"edition_cashless_configs">;
          name?: string;
        },
        null | {
          balance?: { amount: number; decimals: number; symbol: string };
          canTopUp: boolean;
          name?: string;
          refund?: {
            canAskRefund: boolean;
            end?: string;
            isAuthorized: boolean;
            isAutomatic: boolean;
            start?: string;
          };
          shortTag?: string;
          walletId: string;
        }
      >;
      getAccountHistory: FunctionReference<
        "action",
        "public",
        {
          configId: Id<"edition_cashless_configs">;
          limit?: number;
          offset?: number;
        },
        {
          entries: Array<{
            amount: number;
            currency: { decimals: number; symbol: string };
            date: string;
            entryId: string;
            isOnline?: boolean;
            items?: Array<{ amount: number; name: string; quantity: number }>;
            kind: "topup" | "purchase";
            label: string;
            walletName?: string;
          }>;
          hasMore: boolean;
          totalCount?: number;
        }
      >;
      createAccountTopUp: FunctionReference<
        "action",
        "public",
        {
          amountCents: number;
          clientRef: string;
          configId: Id<"edition_cashless_configs">;
          locale: string;
          walletId: string;
        },
        {
          currentBalance?: number;
          expiresAt?: string;
          paymentUrl: string;
          topUpId: Id<"cashless_topups">;
        }
      >;
      listMyTopUps: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        Array<{
          amount: number;
          createdAt: string;
          currency: string;
          editionId: Id<"editions">;
          editionName?: string;
          status:
            | "pending"
            | "succeeded"
            | "failed"
            | "cancelled"
            | "expired"
            | "unknown";
          topUpId: Id<"cashless_topups">;
        }>
      >;
    };
  };
  backstage: {
    editionAnalytics: {
      getEditionStats: FunctionReference<
        "query",
        "public",
        { editionSlug: string; festivalSlug: string },
        any
      >;
      maybeRefreshEditionStats: FunctionReference<
        "mutation",
        "public",
        { editionSlug: string; festivalSlug: string },
        any
      >;
    };
    location: {
      search: FunctionReference<
        "action",
        "public",
        { countryCode?: string; query: string; sessionId: string },
        Array<{ id: string; place_name: string }>
      >;
      details: FunctionReference<
        "action",
        "public",
        { id: string; sessionId: string },
        {
          addressComponents: Array<{
            languageCode: string;
            longText: string;
            shortText: string;
            types: Array<string>;
          }>;
          formattedAddress: string;
          id: string;
          location: { latitude: number; longitude: number };
          postalAddress?: {
            addressLines?: Array<string>;
            administrativeArea?: string;
            languageCode?: string;
            locality?: string;
            postalCode?: string;
            regionCode?: string;
            sublocality?: string;
          };
          shortFormattedAddress: string;
        }
      >;
    };
    social: {
      generateTemplates: FunctionReference<
        "action",
        "public",
        { height: number; prompts: Array<string>; width: number },
        Array<{
          id: string;
          signedPreviewUrl: string;
          variables: Array<{
            defaultValue: string;
            name: string;
            type: string;
          }>;
        }>
      >;
      generatePosts: FunctionReference<
        "action",
        "public",
        {
          items: Array<{
            format: string;
            height: number;
            templateId: string;
            tv?: string;
            variables: any;
            width: number;
          }>;
        },
        Array<{ expiresAt: number; templateId: string; url: string }>
      >;
      getTemplatePreview: FunctionReference<
        "action",
        "public",
        { height: number; templateId: string; variables: any; width: number },
        { expiresAt: number; url: string }
      >;
    };
    editionAssets: {
      generateEditionAssets: FunctionReference<
        "action",
        "public",
        { editionId: number },
        { count: number; success: boolean }
      >;
      refreshCategoryAssets: FunctionReference<
        "action",
        "public",
        { category: string; editionId: number },
        { count: number; success: boolean }
      >;
    };
    artists: {
      fetchSpotifyArtist: FunctionReference<
        "action",
        "public",
        { spotifyId: string },
        { id: string; images?: Array<{ url: string }>; name: string } | null
      >;
      searchArtists: FunctionReference<
        "action",
        "public",
        { query: string },
        Array<{
          editions?: Array<{ _id: string; name: string }>;
          id: string;
          image_url?: string;
          name: string;
          source: "db" | "spotify";
          spotifyId?: string;
        }>
      >;
    };
  };
  test: {
    createBsE2eFixture: FunctionReference<
      "mutation",
      "public",
      { email: string; runId: string; sharedSecret?: string },
      {
        bootstrapEditionId: number;
        bootstrapEditionSlug: string;
        email: string;
        festivalId: number;
        festivalSlug: string;
        premiumLimitedEditionId: number;
        premiumLimitedEditionSlug: string;
        runId: string;
      }
    >;
    seedBsE2eAssets: FunctionReference<
      "mutation",
      "public",
      { editionId: number; runId: string; sharedSecret?: string },
      { savedName: string; socialName: string; success: boolean }
    >;
    cleanupBsE2eFixture: FunctionReference<
      "mutation",
      "public",
      {
        email?: string;
        festivalId?: number;
        festivalSlug?: string;
        runId?: string;
        sharedSecret?: string;
      },
      { deletedEditions: number; deletedFestivals: number; success: boolean }
    >;
  };
  testNode: {
    generateBsE2eSignInLink: FunctionReference<
      "action",
      "public",
      { email: string; redirectUrl: string; sharedSecret?: string },
      { email: string; link: string }
    >;
  };
  actions: {
    admin: {
      festivals: {
        getFestivalsPage: FunctionReference<
          "action",
          "public",
          { cursor?: string },
          any
        >;
        searchFestivals: FunctionReference<
          "action",
          "public",
          { query: string },
          Array<{
            id: number;
            latest_edition: null | {
              city: string | null;
              slug: string;
              year: number;
            };
            name: string;
            objectID: string;
            slug: string;
          }>
        >;
      };
    };
  };
  shorts: {
    actions: {
      generatePresignedViewUrl: FunctionReference<
        "action",
        "public",
        { s3Bucket: string; s3Key: string },
        any
      >;
      retryShortJob: FunctionReference<
        "action",
        "public",
        { jobId: Id<"short_jobs"> },
        any
      >;
    };
    backstageQueries: {
      browseUploads: FunctionReference<
        "query",
        "public",
        {
          editionId: Id<"editions">;
          festUserId?: Id<"fest_users">;
          mediaType?: "video" | "image";
          paginationOpts: {
            cursor: string | null;
            endCursor?: string | null;
            id?: number;
            maximumBytesRead?: number;
            maximumRowsRead?: number;
            numItems: number;
          };
          searchText?: string;
        },
        any
      >;
    };
    queries: {
      myShortJobs: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
      myShorts: FunctionReference<
        "query",
        "public",
        Record<string, never>,
        any
      >;
    };
  };
  crew: {
    editionStats: {
      getEditionHeadlineStats: FunctionReference<
        "query",
        "public",
        { editionSlug: string; festivalSlug: string },
        any
      >;
      refreshEditionHeadlineStats: FunctionReference<
        "mutation",
        "public",
        { editionSlug: string; festivalSlug: string },
        any
      >;
    };
  };
};
export type InternalApiType = {};
